import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Logo from "../components/Logo";

/**
 * Écran d'accueil / Big screen — destiné à être projeté pendant l'événement.
 * Affiche stats temps réel : inscrits, présents, QR scannés, taux de présence.
 * - Refresh automatique toutes les 5s
 * - Animations de pulsation quand le nombre de présents augmente
 * - Notification flash "Nouvelle entrée" avec nom complet pendant ~5s
 * - Phrase d'engagement adaptative selon le taux de présence
 */
export default function WelcomeScreenPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [eventTitle, setEventTitle] = useState("");
  const [lastEntry, setLastEntry] = useState(null);
  const [now, setNow] = useState(new Date());

  // Pour l'effet "live arrival"
  const prevPresentRef = useRef(0);
  const [pulse, setPulse] = useState(false);
  const [flash, setFlash] = useState(null); // {first_name, last_name}
  const flashTimerRef = useRef(null);
  const lastEntryNumberRef = useRef(null);

  useEffect(() => {
    if (!user) nav("/admin/connexion");
    else if (!["admin", "responsable"].includes(user.role)) nav("/admin");
  }, [user, nav]);

  useEffect(() => {
    api.get("/admin/events").then((r) => {
      setEvents(r.data);
      const f = r.data.find((e) => e.is_featured) || r.data[0];
      if (f) { setEventId(f.id); setEventTitle(f.title); }
    });
  }, []);

  useEffect(() => {
    if (!eventId) return;
    const refresh = async () => {
      try {
        const r = await api.get(`/admin/events/${eventId}/live-stats`);
        setStats(r.data);
        // Détection nouvelle entrée → pulsation + flash
        if (prevPresentRef.current && r.data.total_present > prevPresentRef.current) {
          setPulse(true);
          setTimeout(() => setPulse(false), 2500);
        }
        prevPresentRef.current = r.data.total_present;
      } catch (e) { /* ignore */ }

      try {
        const rr = await api.get(`/admin/registrations?event_id=${eventId}`);
        const scanned = (rr.data || [])
          .filter((x) => x.checked_in_at)
          .sort((a, b) => (b.checked_in_at || "").localeCompare(a.checked_in_at || ""));
        const last = scanned[0];
        if (last) {
          // Si c'est une nouvelle dernière entrée → flash
          if (lastEntryNumberRef.current && last.registration_number !== lastEntryNumberRef.current) {
            setFlash({ first_name: last.first_name, last_name: last.last_name, at: last.checked_in_at });
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
            flashTimerRef.current = setTimeout(() => setFlash(null), 6000);
          }
          lastEntryNumberRef.current = last.registration_number;
          setLastEntry(last);
        }
      } catch (e) { /* ignore */ }
    };
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [eventId]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!user) return null;
  const taux = stats && stats.total_registered > 0
    ? Math.round((stats.total_present / stats.total_registered) * 100) : 0;

  // Phrase d'engagement adaptative
  const motivation = stats ? (
    stats.total_present === 0 ? "Premiers participants en route…" :
    taux < 10 ? `${stats.total_present} déjà sur place — la rencontre commence` :
    taux < 30 ? `${stats.total_present} déjà présents — l'événement prend vie` :
    taux < 60 ? `${stats.total_present} présents — l'enthousiasme est là` :
    taux < 90 ? `${stats.total_present} présents — salle bientôt comble` :
    "Salle comble — l'engagement de la diaspora"
  ) : "Préparation en cours…";

  return (
    <div className="min-h-screen bg-ink text-white relative overflow-hidden" data-testid="welcome-screen">
      {/* Decorative */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_20%,#D4AF37,transparent_50%)]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      {/* FLASH OVERLAY — Nouvelle entrée live */}
      {flash && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none animate-flash"
             data-testid="welcome-flash">
          <div className="bg-emerald-500 text-white px-10 py-8 rounded-sm shadow-2xl border-4 border-gold text-center">
            <div className="text-[0.65rem] uppercase tracking-[0.4em] text-gold mb-2">Nouvelle entrée</div>
            <div className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
              {flash.first_name} {flash.last_name}
            </div>
            <div className="mt-3 text-sm sm:text-base opacity-90">vient d&apos;arriver — bienvenue 🎉</div>
          </div>
        </div>
      )}

      {/* TOP BAR */}
      <header className="relative px-6 lg:px-12 py-5 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <Logo className="w-12 h-12 lg:w-14 lg:h-14" />
          <div>
            <div className="text-[0.65rem] lg:text-xs uppercase tracking-[0.3em] text-gold">Front Uni pour la RDC</div>
            <div className="font-serif text-base lg:text-xl">{eventTitle || "Croisade Scientifique"}</div>
          </div>
        </div>
        <div className="text-right">
          {events.length > 1 && (
            <select value={eventId} onChange={(e) => {
              const ev = events.find((x) => x.id === e.target.value);
              setEventId(e.target.value); setEventTitle(ev?.title || "");
            }} data-testid="welcome-event-select"
              className="bg-transparent border border-white/20 rounded-sm px-2 py-1 text-xs mb-1">
              {events.map((e) => <option key={e.id} value={e.id} className="text-ink">{e.title}</option>)}
            </select>
          )}
          <div className="font-mono text-lg lg:text-2xl text-gold">{now.toLocaleTimeString("fr-FR")}</div>
          <div className="text-[0.65rem] lg:text-xs text-white/60">{now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
      </header>

      {/* CONTENU */}
      <main className="relative px-6 lg:px-12 py-10 lg:py-16">
        <div className="text-center mb-12 lg:mb-16">
          <div className="text-[0.7rem] uppercase tracking-[0.4em] text-gold mb-4">Bienvenue</div>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[1.05] tracking-tight max-w-6xl mx-auto">
            Bienvenue à la <span className="text-gold italic font-light">Croisade Scientifique</span>
          </h1>
          <p className="mt-6 text-base lg:text-lg text-white/70 max-w-2xl mx-auto font-serif italic">
            « Révision constitutionnelle ou changement de la Constitution en RDC : pourquoi, pourquoi pas ? »
          </p>
          {/* Phrase d'engagement live */}
          <div className="mt-8 inline-block px-6 py-3 bg-gold/10 border border-gold/30 rounded-sm">
            <div className="text-[0.6rem] uppercase tracking-[0.3em] text-gold/80">Mobilisation en direct</div>
            <div className="font-serif text-lg lg:text-2xl text-white mt-1" data-testid="welcome-motivation">
              {motivation}
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 max-w-7xl mx-auto">
          <Stat label="Inscrits" value={stats?.total_registered ?? "—"} accent="white" tid="welcome-stat-registered" />
          <Stat label="Présents" value={stats?.total_present ?? "—"} accent="gold" pulse={pulse} tid="welcome-stat-present" />
          <Stat label="Taux de présence" value={stats ? `${taux}%` : "—"} accent="green" tid="welcome-stat-rate" />
          <Stat label="QR validés" value={stats?.total_present ?? "—"} accent="white" tid="welcome-stat-scanned" />
        </div>

        {/* Progress bar */}
        {stats && stats.total_registered > 0 && (
          <div className="mt-10 max-w-5xl mx-auto" data-testid="welcome-progress">
            <div className="flex items-center justify-between text-[0.65rem] uppercase tracking-[0.25em] text-white/55 mb-2">
              <span>Progression de la participation</span>
              <span>{stats.total_present} / {stats.total_registered}</span>
            </div>
            <div className="h-3 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-gold via-gold to-emerald-400 transition-all duration-1000"
                   style={{ width: `${Math.min(100, taux)}%` }} />
            </div>
          </div>
        )}

        {/* Dernière entrée */}
        <div className="mt-12 lg:mt-16 max-w-3xl mx-auto bg-white/5 border border-white/10 rounded-sm p-6 lg:p-8 text-center" data-testid="welcome-last-entry">
          <div className="text-[0.6rem] uppercase tracking-[0.3em] text-gold mb-2">Dernière entrée scannée</div>
          {lastEntry ? (
            <>
              <div className="font-serif text-2xl lg:text-3xl">{lastEntry.first_name} {lastEntry.last_name}</div>
              <div className="text-sm text-white/60 mt-2 font-mono">
                {new Date(lastEntry.checked_in_at).toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </div>
            </>
          ) : (
            <div className="text-white/45 italic text-sm">En attente du premier scan…</div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-[0.6rem] uppercase tracking-[0.4em] text-white/35">
          Diaspora congolaise · Plaidoyer international · {new Date().getFullYear()}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, accent, pulse, tid }) {
  const color = { gold: "text-gold", green: "text-emerald-400", white: "text-white" }[accent] || "text-white";
  return (
    <div className={`bg-white/5 border border-white/10 rounded-sm p-5 lg:p-8 text-center transition-all ${pulse ? "ring-4 ring-gold scale-105 bg-gold/10" : ""}`}
         data-testid={tid}>
      <div className="text-[0.55rem] lg:text-[0.65rem] uppercase tracking-[0.3em] text-white/60 mb-3">{label}</div>
      <div className={`font-serif text-5xl lg:text-7xl xl:text-8xl font-light ${color} leading-none ${pulse ? "animate-pulse" : ""}`}>
        {value}
      </div>
    </div>
  );
}
